import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { Check, RotateCcw, Flag } from "lucide-react";

const fieldCls = "h-12 text-base bg-[#181A22] border-white/10";

const F = ({ label, k, f, set, ...rest }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <Input value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={`mt-1 ${fieldCls}`} data-testid={`edit-${k}`} {...rest} />
  </label>
);

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h3 className="text-sm font-bold uppercase tracking-wider text-[#E63946]">{title}</h3>
    {children}
  </section>
);

const Status = ({ job, onStatus }) => {
  const [busy, setBusy] = useState(false);
  const closed = job.status === "closed";
  const go = async () => {
    if (!window.confirm(closed ? "Riaprire il lavoro?" : "Confermi la fine lavoro? Le ore verranno bloccate.")) return;
    setBusy(true);
    try {
      const { data } = closed ? await api.post(`/admin/bodyshop/${job.id}/reopen`) : await api.post(`/admin/bodyshop/${job.id}/close`, job.final || {});
      onStatus(data.job?.status ?? (closed ? "open" : "closed"));
      toast.success(closed ? "Lavoro riaperto" : "Lavoro segnato come finito");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#181A22] p-3">
      <span className={`text-sm font-semibold mr-auto ${closed ? "text-[#4ADE80]" : "text-[#FF9E00]"}`} data-testid="edit-status-label">{closed ? "Fine lavoro" : "In lavorazione"}</span>
      <Button type="button" variant="outline" disabled={busy} onClick={go} className="rounded-full h-11 border-white/15 bg-transparent" data-testid="edit-status-toggle">
        {closed ? <><RotateCcw className="w-4 h-4 mr-1.5" />Riapri</> : <><Flag className="w-4 h-4 mr-1.5" />Fine lavoro</>}
      </Button>
    </div>
  );
};

const useFocusField = (open, k) => useEffect(() => {
  if (!open || !k) return;
  const t = setTimeout(() => {
    const el = document.querySelector(`[data-testid="edit-${k}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    el.focus({ preventScroll: true });
    el.closest("label")?.classList.add("ring-2", "ring-[#E63946]", "rounded-md");
    setTimeout(() => el.closest("label")?.classList.remove("ring-2", "ring-[#E63946]", "rounded-md"), 1800);
  }, 350);
  return () => clearTimeout(t);
}, [open, k]);

export const JobEditSheet = ({ open, onOpenChange, f, set, isNew, onSave, busy, onStatus, focusKey }) => {
  useFocusField(open, focusKey);
  return (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full sm:max-w-lg bg-[#12141A] border-white/10 p-0 flex flex-col [&>button]:top-3 [&>button]:right-3 [&>button]:p-3 [&>button]:rounded-full [&>button>svg]:w-5 [&>button>svg]:h-5" data-testid="job-edit-sheet">
      <SheetHeader className="px-5 pt-5 pb-3 border-b border-white/10">
        <SheetTitle className="font-display uppercase text-left">Modifica scheda {f.number}</SheetTitle>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-7">
        {!isNew && <Section title="Stato del lavoro"><Status job={f} onStatus={onStatus} /></Section>}
        <Section title="Cliente">
          <F label="Nome cliente" k="customer_name" f={f} set={set} />
          <F label="Telefono" k="customer_phone" f={f} set={set} type="tel" inputMode="tel" />
        </Section>
        <Section title="Auto">
          <F label="Targa" k="plate" f={f} set={set} autoCapitalize="characters" />
          <div className="grid grid-cols-2 gap-3">
            <F label="Marca" k="brand" f={f} set={set} />
            <F label="Modello" k="model" f={f} set={set} />
            <F label="Colore" k="color" f={f} set={set} />
            <F label="Km" k="km" f={f} set={set} inputMode="numeric" />
          </div>
          <F label="Telaio" k="vin" f={f} set={set} autoCapitalize="characters" />
        </Section>
        <Section title="Lavori e ricambi">
          {[["works", "Elenco lavori", 8], ["parts", "Lista ricambi", 4]].map(([k, l, r]) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{l} · una voce per riga</span>
              <Textarea rows={r} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 text-base bg-[#181A22] border-white/10" data-testid={`edit-${k}`} />
            </label>
          ))}
        </Section>
        <Section title="Preventivo e date">
          <F label="N. scheda" k="number" f={f} set={set} />
          <F label="Preventivo € (+IVA)" k="quote_total" f={f} set={set} inputMode="decimal" />
          {[["date_in", "Data ingresso"], ["date_out", "Consegna prevista"]].map(([k, l]) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{l}</span>
              <DateInput value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 h-12 text-base bg-[#181A22] border-white/10" data-testid={`edit-${k}`} />
            </label>
          ))}
          <p className="text-xs text-muted-foreground">Ore lavorate e consuntivo si calcolano da soli dai fogli ore.</p>
        </Section>
      </div>
      <div className="border-t border-white/10 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <Button onClick={onSave} disabled={busy} className="w-full h-12 rounded-full bg-[#E63946] hover:bg-[#D62828] text-white text-base" data-testid="edit-save-btn">
          <Check className="w-5 h-5 mr-1.5" />Salva modifiche
        </Button>
      </div>
    </SheetContent>
  </Sheet>
  );
};
